import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DeliverableImportTypes } from '@mono/proto-clients';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useChatFeedback } from '../../ChatFeedbackContext';
import { deliverableImportFeedback } from './card';
import { actionLabel } from './proposalShared';
import { PROGRAM_LEVEL_KEY, workstreamLabel } from './shared';
import { deliverableImportCommitResultKey } from './useReview';
const { DeliverableImportAction, DeliverableImportDecision, DeliverableCommitStatus } = DeliverableImportTypes;
function statusLabel(status) {
    switch(status){
        case DeliverableCommitStatus.CREATED:
            return 'Created';
        case DeliverableCommitStatus.UPDATED:
            return 'Updated';
        case DeliverableCommitStatus.FAILED:
            return 'Failed';
        default:
            return 'Unspecified';
    }
}
/**
 * Reconstructs an equivalent CommitDeliverableImportWorkstreamResponse from
 * the review's per-proposal `commit_result` values — the proto records these
 * on every committed/commit-failed proposal exactly so this fallback is
 * possible when the cached response from the commit call itself is gone
 * (e.g. the page was reloaded after the agent emitted this card).
 */ function reconstructFromReview(review, workstreamKey) {
    const workstream = review.workstreams.find((w)=>w.key === workstreamKey);
    const results = review.proposals.filter((p)=>p.workstreamKey === workstreamKey && (p.decision === DeliverableImportDecision.COMMITTED || p.decision === DeliverableImportDecision.COMMIT_FAILED) && p.commitResult).map((p)=>p.commitResult);
    if (results.length === 0) return null;
    return new DeliverableImportTypes.CommitDeliverableImportWorkstreamResponse({
        results,
        workstream,
        created: results.filter((r)=>r.status === DeliverableCommitStatus.CREATED).length,
        updated: results.filter((r)=>r.status === DeliverableCommitStatus.UPDATED).length,
        failed: results.filter((r)=>r.status === DeliverableCommitStatus.FAILED).length,
        verified: results.filter((r)=>r.verified).length
    });
}
/**
 * COMMIT_RESULT body, rendered from the COMMIT RESPONSE rather than the
 * review, per the task: a held response cached at commit time, falling back
 * to reconstructing from `review.proposals[].commit_result` (recorded on
 * every attempted proposal for exactly this purpose) when the cache entry is
 * gone — e.g. after a reload.
 */ export function CommitResultCard({ payload, review }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const sendFeedback = useChatFeedback();
    const cached = queryClient.getQueryData(deliverableImportCommitResultKey(payload.programId, payload.changeSetId, payload.workstreamKey));
    const reconstructed = cached ? null : reconstructFromReview(review, payload.workstreamKey);
    const response = cached ?? reconstructed;
    const ws = review.workstreams.find((w)=>w.key === payload.workstreamKey);
    const label = ws ? workstreamLabel(ws) : payload.workstreamKey;
    if (!response) {
        return /*#__PURE__*/ _jsx("div", {
            className: "mt-2 text-sm text-gray-500",
            children: "No commit results are available for this workstream yet."
        });
    }
    const proposals = review.proposals.filter((p)=>p.workstreamKey === payload.workstreamKey);
    const titleFor = (proposalId)=>proposals.find((p)=>p.id === proposalId)?.proposed?.title || 'Deliverable';
    const deliverableNumberFor = (r)=>r.saved?.deliverableNumber || proposals.find((p)=>p.id === r.proposalId)?.proposed?.deliverableNumber || '—';
    const rejectedCount = proposals.filter((p)=>p.decision === DeliverableImportDecision.REJECTED).length;
    const pendingCount = proposals.filter((p)=>p.decision === DeliverableImportDecision.PENDING).length;
    const alreadyMatchesCount = proposals.filter((p)=>p.action === DeliverableImportAction.ALREADY_MATCHES).length;
    // Failures first — never buried at the bottom of a long table.
    const results = [
        ...response.results
    ].sort((a, b)=>{
        const aFailed = a.status === DeliverableCommitStatus.FAILED ? 0 : 1;
        const bFailed = b.status === DeliverableCommitStatus.FAILED ? 0 : 1;
        return aFailed - bFailed;
    });
    const failures = results.filter((r)=>r.status === DeliverableCommitStatus.FAILED);
    const handleViewDeliverables = ()=>{
        const base = `/delivery/programs/${payload.programId}/deliverables`;
        // A committed duty's deliverable number carries the workstream key as its
        // prefix (e.g. "CAATS-5001AH-DEL-1-02") — nothing else on an imported
        // duty does, so that's what the Deliverables page's search must match on.
        // Program-level duties carry no workstream token at all (a bare base like
        // "0001AA-DEL-1-01"), so there's nothing to filter by; show everything.
        const isProgramLevel = payload.workstreamKey === PROGRAM_LEVEL_KEY;
        navigate(isProgramLevel ? base : `${base}?${new URLSearchParams({
            q: `${payload.workstreamKey}-`
        }).toString()}`);
        sendFeedback(deliverableImportFeedback({
            event: 'navigate',
            changeSetId: payload.changeSetId,
            workstreamKey: payload.workstreamKey,
            text: isProgramLevel ? 'Opened all deliverables.' : `Opened ${label} deliverables.`
        }));
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-2 space-y-3 text-sm text-gray-700",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "font-medium text-gray-900",
                children: [
                    label,
                    " Updated"
                ]
            }),
            reconstructed && /*#__PURE__*/ _jsx("div", {
                className: "text-xs text-gray-500 italic",
                children: "Reconstructed from the staged proposals — the original commit response wasn't found in this session, so this reflects what's known now."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-4 gap-2 text-xs",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border rounded-md p-2 bg-gray-50",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-gray-500",
                                children: "Created"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-base font-semibold text-gray-900",
                                children: response.created
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border rounded-md p-2 bg-gray-50",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-gray-500",
                                children: "Updated"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-base font-semibold text-gray-900",
                                children: response.updated
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: `border rounded-md p-2 ${response.failed > 0 ? 'bg-red-50' : 'bg-gray-50'}`,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-gray-500",
                                children: "Failed"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: `text-base font-semibold ${response.failed > 0 ? 'text-red-700' : 'text-gray-900'}`,
                                children: response.failed
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border rounded-md p-2 bg-gray-50",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-gray-500",
                                children: "Verified"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-base font-semibold text-gray-900",
                                children: response.verified
                            })
                        ]
                    })
                ]
            }),
            failures.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "border-2 border-red-400 rounded-md p-2 bg-red-50 space-y-1",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-xs font-semibold text-red-800",
                        children: [
                            failures.length,
                            " deliverable",
                            failures.length === 1 ? '' : 's',
                            " failed to commit"
                        ]
                    }),
                    failures.map((f)=>/*#__PURE__*/ _jsxs("div", {
                            className: "text-xs text-red-700",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "font-medium",
                                    children: [
                                        titleFor(f.proposalId),
                                        ":"
                                    ]
                                }),
                                ' ',
                                f.error || 'Unknown error'
                            ]
                        }, f.proposalId))
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-xs text-gray-500",
                children: [
                    "Unchanged: ",
                    rejectedCount,
                    " rejected, ",
                    pendingCount,
                    " still pending, ",
                    alreadyMatchesCount,
                    ' ',
                    "already matching."
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-auto max-h-64 border rounded-md",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full text-xs",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "sticky top-0 bg-gray-50 text-gray-500",
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left font-medium px-2 py-1",
                                        children: "Deliverable Number"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left font-medium px-2 py-1",
                                        children: "Deliverable"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left font-medium px-2 py-1",
                                        children: "Action"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left font-medium px-2 py-1",
                                        children: "Saved Result"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: results.map((r)=>{
                                const failed = r.status === DeliverableCommitStatus.FAILED;
                                return /*#__PURE__*/ _jsxs("tr", {
                                    "data-testid": Testids.deliverableImport.resultRow,
                                    "data-status": statusLabel(r.status).toLowerCase(),
                                    className: `border-t ${failed ? 'bg-red-50' : ''}`,
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-gray-900",
                                            children: deliverableNumberFor(r)
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-gray-900",
                                            children: titleFor(r.proposalId)
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-gray-700",
                                            children: actionLabel(r.action)
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: `px-2 py-1 ${failed ? 'text-red-700 font-medium' : 'text-gray-700'}`,
                                            children: failed ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    "⚠ Failed: ",
                                                    r.error || 'Unknown error'
                                                ]
                                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    "Due ",
                                                    r.saved?.firstDueDate || '—',
                                                    " · ",
                                                    r.savedOccurrenceDates.length,
                                                    ' ',
                                                    "occurrence",
                                                    r.savedOccurrenceDates.length === 1 ? '' : 's',
                                                    " —",
                                                    ' ',
                                                    r.verified ? '✓ verified' : `Mismatch: ${r.mismatches.join(', ')}`
                                                ]
                                            })
                                        })
                                    ]
                                }, r.proposalId);
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-testid": Testids.deliverableImport.viewDeliverables,
                onClick: handleViewDeliverables,
                className: "inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors",
                children: payload.workstreamKey === PROGRAM_LEVEL_KEY ? 'View All Deliverables' : `View ${label} Deliverables`
            })
        ]
    });
}
